import venueExterior from "@/assets/pardue-center-new-green.webp";
import venueBallroom from "@/assets/ballroom-new.jpg";
import venueInterior from "@/assets/ballroom-interior.jpg";
import venueAerial from "@/assets/pavilion-new.jpeg";
import venueConference from "@/assets/conference-room-updated.jpg";
import { MapPin, Users, Car, Accessibility, ChefHat, Flower2 } from "lucide-react";

const Venue = () => {
  const venueFeatures = [
    { icon: <Flower2 className="w-5 h-5" />, text: "Elegant ballroom with natural light" },
    { icon: <MapPin className="w-5 h-5" />, text: "Outdoor ceremony & reception space" },
    { icon: <Users className="w-5 h-5" />, text: "Capacity: up to 150 seated guests" },
    { icon: <ChefHat className="w-5 h-5" />, text: "Prep kitchen & catering access" },
    { icon: <Accessibility className="w-5 h-5" />, text: "Full ADA accessibility" },
    { icon: <Car className="w-5 h-5" />, text: "Ample parking available" },
  ];

  const venueSpaces = [
    {
      name: "Ballroom",
      description: "Elegant space perfect for weddings, receptions, and large celebrations",
    },
    {
      name: "Pavilion",
      description: "Beautiful outdoor covered space ideal for ceremonies and casual gatherings",
    },
    {
      name: "Combined Spaces",
      description: "Ballroom + Pavilion for grand events requiring both indoor and outdoor areas",
    },
    {
      name: "Conference & Meeting Rooms",
      description: "Professional spaces for corporate events, workshops, and intimate gatherings",
    },
  ];

  return (
    <section id="venue" className="section-padding bg-muted/30">
      <div className="container-elegant">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-accent font-medium tracking-widest uppercase text-sm mb-3">
            Our Exclusive Home
          </p>
          <h2 className="heading-section text-foreground mb-4">
            The Pardue Family Foundation Event Center
          </h2>
          <div className="flex justify-center mb-6">
            <span className="text-accent text-3xl">⚜</span>
          </div>
          <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
            A newly renovated, multi-purpose venue in Milton, Georgia.
            Modern farmhouse charm meets flexible event layouts—perfect for
            weddings, quinceañeras, corporate functions, and private celebrations.
          </p>
        </div>

        {/* Venue Images */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          <div className="rounded-2xl overflow-hidden shadow-elevated md:col-span-2 lg:col-span-2">
            <img
              src={venueExterior}
              alt="The Pardue Center exterior with pavilion"
              className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="rounded-2xl overflow-hidden shadow-elevated">
            <img
              src={venueBallroom}
              alt="The Pardue Center ballroom with chandeliers"
              className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="rounded-2xl overflow-hidden shadow-elevated">
            <img
              src={venueInterior}
              alt="The Pardue Center interior lounge area"
              className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="rounded-2xl overflow-hidden shadow-elevated">
            <img
              src={venueAerial}
              alt="Aerial view of The Pardue Center"
              className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="rounded-2xl overflow-hidden shadow-elevated">
            <img
              src={venueConference}
              alt="The Pardue Center conference room"
              className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
        </div>

        {/* Features */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16 max-w-4xl mx-auto">
          {venueFeatures.map((feature, index) => (
            <div
              key={index}
              className="flex items-center gap-3 bg-card p-4 rounded-lg border border-border/50"
            >
              <span className="text-accent">{feature.icon}</span>
              <span className="text-foreground">{feature.text}</span>
            </div>
          ))}
        </div>

        {/* Included With Rental */}
        <div className="bg-card rounded-2xl p-8 mb-12 max-w-4xl mx-auto border border-border/50">
          <h3 className="font-serif text-2xl font-semibold text-foreground text-center mb-6">
            Included With Every Rental
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              "Standard tables & chairs",
              "Basic setup & breakdown",
              "Venue representative on site",
              "Access to prep kitchen (for Gracious Gatherings catering)",
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-accent">✓</span>
                <span className="text-muted-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Available Spaces */}
        <h3 className="font-serif text-2xl font-semibold text-foreground text-center mb-8">
          Available Spaces
        </h3>
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-12">
          {venueSpaces.map((space, index) => (
            <div
              key={index}
              className="card-elevated p-6 hover:shadow-lg transition-shadow"
            >
              <h4 className="font-serif text-xl font-semibold text-primary mb-3">
                {space.name}
              </h4>
              <p className="text-muted-foreground">{space.description}</p>
            </div>
          ))}
        </div>

        {/* Call for Pricing */}
        <div className="bg-primary/10 rounded-2xl p-8 max-w-2xl mx-auto text-center border border-primary/20">
          <h4 className="font-serif text-2xl font-semibold text-foreground mb-3">
            Venue Pricing
          </h4>
          <p className="text-muted-foreground mb-4">
            We offer competitive pricing with special rates for non-profit organizations and schools.
          </p>
          <a
            href="tel:+17704410401"
            className="btn-gold px-8 py-3 rounded-full text-lg font-semibold inline-block hover:scale-105 transition-transform"
          >
            Call for Pricing
          </a>
          <p className="text-muted-foreground text-sm mt-4">
            (770) 441-0401
          </p>
        </div>
      </div>
    </section>
  );
};

export default Venue;
